import { useState, useCallback, useRef } from 'react';

export interface UndoAction {
  type: string;
  description: string;
  undo: () => Promise<void> | void;
  redo: () => Promise<void> | void;
}

const MAX_HISTORY = 30;

export function useUndoRedo() {
  const undoStack = useRef<UndoAction[]>([]);
  const redoStack = useRef<UndoAction[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const pushAction = useCallback((action: UndoAction) => {
    undoStack.current.push(action);
    if (undoStack.current.length > MAX_HISTORY) {
      undoStack.current.shift();
    }
    redoStack.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(async () => {
    const action = undoStack.current.pop();
    if (!action) return;

    await action.undo();
    redoStack.current.push(action);
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(true);

    // Announce to screen readers
    const announcer = document.getElementById('announcer');
    if (announcer) {
      announcer.textContent = `Desfeito: ${action.description}`;
    }
  }, []);

  const redo = useCallback(async () => {
    const action = redoStack.current.pop();
    if (!action) return;

    await action.redo();
    undoStack.current.push(action);
    setCanUndo(true);
    setCanRedo(redoStack.current.length > 0);

    // Announce to screen readers
    const announcer = document.getElementById('announcer');
    if (announcer) {
      announcer.textContent = `Refeito: ${action.description}`;
    }
  }, []);

  const clearHistory = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  return { pushAction, undo, redo, canUndo, canRedo, clearHistory };
}
